import { useI18N } from "@sundae/react-hooks";
import {
  Badge,
  Balance,
  ConditionalWrapper,
  InfoCircledIcon,
  Text,
  Tooltip,
  toolkitCx,
} from "@sundae/ui-toolkit";
import { IMappedAsset } from "@sundae/wallet-react";
import cx from "classnames";
import truncate from "lodash/truncate";
import React, { useMemo } from "react";

import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import { ADA_METADATA } from "../../constants/cardano.constants";
import { useAdaBalance } from "../../hooks/useAdaBalance";
import { useProfileAppContext } from "../../stores";
import { IAssetMetaData } from "../../types/Asset.types";
import { assetHasQuantity, getAssetName } from "../../utils/assets.utils";
import AssetAvatar from "../AssetAvatar";
import AssetMetaDataWarning from "../AssetMetaDataWarning";
import { IAssetSelectFlow } from "../AssetSelectFlow/AssetSelectFlow";
import AvailableADABalanceTooltip from "../AvailableADABalanceTooltip/AvailableADABalanceTooltip";

export interface IAssetSelectItem extends Pick<IAssetSelectFlow, "onAssetSelect"> {
  isActiveAsset: boolean;
  allowUnselect?: boolean;
  asset: IMappedAsset<IAssetMetaData>;
  currentLocale?: string;
  isLargeScreen?: boolean;
}

const AssetSelectItem: React.FC<IAssetSelectItem> = ({
  isActiveAsset,
  allowUnselect,
  asset,
  currentLocale,
  isLargeScreen,
  onAssetSelect,
}) => {
  const { t } = useI18N("glossary");
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const isAssetAda = useMemo(() => SundaeUtils.isAdaAsset(asset.metadata), [asset]);
  const { availableAdaBalance } = useAdaBalance();
  const resolvedAssetName = useMemo(() => {
    if (isAssetAda) {
      return ADA_METADATA.assetName;
    }

    return asset.metadata.assetName;
  }, [asset, isAssetAda]);

  if (asset.metadata.assetId !== undefined) {
    return (
      <button
        data-testid={asset.metadata.assetId}
        className={cx(
          "relative w-full select-none p-4",
          "flex items-center gap-2 md:gap-4",
          "rounded-lg",
          toolkitCx.layout.border.weight.default,
          toolkitCx.layout.background.hoverable,
          toolkitCx.layout.transition.default,
          {
            [`cursor-pointer ${toolkitCx.layout.background.card} ${toolkitCx.layout.border.default} ${toolkitCx.recipes.scaleWhenActive}`]:
              !isActiveAsset,
            [`cursor-not-allowed ${toolkitCx.recipes.blurryCorner.success} ${toolkitCx.layout.border.success}`]:
              isActiveAsset,
          }
        )}
        onClick={() => {
          if (allowUnselect || !isActiveAsset) {
            onAssetSelect?.(asset);
          }
        }}
      >
        <AssetAvatar
          asset={asset.metadata}
          className="!h-auto"
          size={isLargeScreen ? "lg" : "md"}
        />

        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col items-start">
            <div className="flex items-baseline gap-2">
              <Text tag="span">
                {isAssetAda ? ADA_METADATA.ticker : getAssetName(asset.metadata, 16, 0)}
              </Text>

              {asset.metadata.warning && <AssetMetaDataWarning warning={asset.metadata.warning} />}

              {isActiveAsset && (
                <Badge className="self-center" size="2xs" variant="success">
                  <CheckCircleIcon className={cx("mr-1 h-3 w-3", toolkitCx.layout.text.success)} />
                  {t("selected", { ns: "common" })}
                </Badge>
              )}
            </div>

            {resolvedAssetName && (
              <Tooltip
                trigger={
                  <Text className="text-left" tag="span" size="2xs" variant="silent">
                    {truncate(resolvedAssetName, { length: 10 })}
                  </Text>
                }
                tooltipLabel={resolvedAssetName}
              />
            )}
          </div>

          {assetHasQuantity(asset) && (
            <ConditionalWrapper
              condition={isAssetAda}
              wrapper={(children) => (
                <AvailableADABalanceTooltip>{children}</AvailableADABalanceTooltip>
              )}
            >
              <div className="grid gap-1 text-right">
                <Text
                  className="flex items-center justify-end gap-[2px]"
                  tag="span"
                  size="2xs"
                  variant="silent"
                >
                  {t("balance", { context: "available" })}

                  {isAssetAda && <InfoCircledIcon className="h-3 w-3" />}
                </Text>
                <Balance
                  abbreviate={false}
                  className="ml-auto"
                  locale={currentLocale}
                  privacy={privacyEnabled}
                  showSymbol={isAssetAda}
                  size="sm"
                  value={isAssetAda ? availableAdaBalance.quantity : asset.quantity}
                />
              </div>
            </ConditionalWrapper>
          )}
        </div>
      </button>
    );
  }

  return null;
};

export default AssetSelectItem;
